import type { MaybeRefOrGetter } from 'vue'

export default function useBusinessMembers(options: { key?: MaybeRefOrGetter<string> } = {}) {
  const { $api } = useNuxtApp()

  const {
    data: members,
    pending: isLoadingMembers,
    refresh: refreshMembers,
  } = useLazyApi('/api/users/me/business/members', {
    key: options.key,
    transform: (response: BusinessMembersResponse) => response.items,
    default: () => [] as MemberItem[],
  })

  const isModifyingMembers = ref(false)

  const addMembers = async (emails: string[]) => {
    isModifyingMembers.value = true
    try {
      const result = await $api('/api/users/me/business/members', {
        method: 'PATCH',
        body: {
          add: emails.map(email => ({ email })),
        },
      })
      await refreshMembers()
      return result
    } finally {
      isModifyingMembers.value = false
    }
  }

  const removeMembers = async (usernames: string[]) => {
    isModifyingMembers.value = true
    try {
      const result = await $api('/api/users/me/business/members', {
        method: 'PATCH',
        body: {
          remove: usernames,
        },
      })
      await refreshMembers()
      return result
    } finally {
      isModifyingMembers.value = false
    }
  }

  const makeAdmins = async (usernames: string[]) => {
    isModifyingMembers.value = true
    try {
      const result = await $api('/api/users/me/business/members', {
        method: 'PATCH',
        body: {
          update: usernames.map(username => ({ username, role: 'admin' as const })),
        },
      })
      await refreshMembers()
      return result
    } finally {
      isModifyingMembers.value = false
    }
  }

  const revokeAdmins = async (usernames: string[]) => {
    isModifyingMembers.value = true
    try {
      const result = await $api('/api/users/me/business/members', {
        method: 'PATCH',
        body: {
          update: usernames.map(username => ({ username, role: 'member' as const })),
        },
      })
      await refreshMembers()
      return result
    } finally {
      isModifyingMembers.value = false
    }
  }

  return {
    members,
    isLoadingMembers,
    refreshMembers,
    addMembers,
    removeMembers,
    makeAdmins,
    revokeAdmins,
    isModifyingMembers,
  }
}
